import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, Webhook, Clock, AlertTriangle, CheckCircle,
  Copy, Check, Package, ShoppingCart, ArrowDownUp, Search
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsShopifySync() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const webhookEvents = [
    {
      event: 'products/create',
      description: 'Fired when a new product is created in Shopify. CI imports the product, all variants, pricing, and images.',
      payload: `{
  "id": 7891234567890,
  "title": "Premium Wireless Headphones",
  "vendor": "AudioTech",
  "product_type": "Electronics",
  "status": "active",
  "variants": [
    {
      "id": 4412345678901,
      "title": "Black",
      "sku": "WH-PRO-BLK",
      "price": "149.99",
      "inventory_quantity": 250,
      "inventory_item_id": 4512345678901
    }
  ],
  "created_at": "2026-03-15T10:30:00-04:00"
}`
    },
    {
      event: 'products/update',
      description: 'Fired when any product field changes — title, description, price, variant, or status. CI updates the matching record.',
      payload: `{
  "id": 7891234567890,
  "title": "Premium Wireless Headphones v2",
  "status": "active",
  "variants": [
    {
      "id": 4412345678901,
      "title": "Black",
      "sku": "WH-PRO-BLK",
      "price": "139.99",
      "inventory_quantity": 245
    },
    {
      "id": 4412345678902,
      "title": "White",
      "sku": "WH-PRO-WHT",
      "price": "139.99",
      "inventory_quantity": 180
    }
  ],
  "updated_at": "2026-03-16T08:15:00-04:00"
}`
    },
    {
      event: 'products/delete',
      description: 'Fired when a product is permanently deleted from Shopify. CI marks the product as deleted and pauses any active deals referencing it.',
      payload: `{
  "id": 7891234567890
}`
    },
    {
      event: 'orders/create',
      description: 'Fired when a new order is placed. CI records the order for revenue tracking, deal attribution, and health score calculation.',
      payload: `{
  "id": 5551234567890,
  "order_number": 1042,
  "financial_status": "paid",
  "total_price": "289.98",
  "currency": "USD",
  "line_items": [
    {
      "id": 1112345678901,
      "product_id": 7891234567890,
      "variant_id": 4412345678901,
      "title": "Premium Wireless Headphones v2",
      "sku": "WH-PRO-BLK",
      "quantity": 2,
      "price": "139.99"
    }
  ],
  "created_at": "2026-03-16T12:00:00-04:00"
}`
    },
    {
      event: 'orders/updated',
      description: 'Fired when an order is modified — fulfillment status changes, refunds, cancellations. CI updates order status and recalculates affected metrics.',
      payload: `{
  "id": 5551234567890,
  "order_number": 1042,
  "financial_status": "partially_refunded",
  "fulfillment_status": "fulfilled",
  "total_price": "289.98",
  "refunds": [
    {
      "id": 8881234567890,
      "created_at": "2026-03-17T09:00:00-04:00",
      "refund_line_items": [
        {
          "line_item_id": 1112345678901,
          "quantity": 1,
          "subtotal": "139.99"
        }
      ]
    }
  ],
  "updated_at": "2026-03-17T09:00:00-04:00"
}`
    },
    {
      event: 'inventory_levels/update',
      description: 'Fired when inventory quantity changes at any location. CI updates stock levels, triggers low-stock alerts, and notifies agents if thresholds are breached.',
      payload: `{
  "inventory_item_id": 4512345678901,
  "location_id": 6612345678901,
  "available": 12,
  "updated_at": "2026-03-17T14:30:00-04:00"
}`
    }
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Shopify Sync
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Product &amp; Inventory Sync
        </h1>
        <p className="text-lg text-[#94A3B8]">
          How Commerce Index keeps your product catalog, orders, and inventory levels synchronized
          with Shopify — in real time via webhooks, with manual re-sync as a fallback.
        </p>
      </div>

      {/* How Sync Works */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          How Sync Works
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#3EF1C7] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Initial Full Sync</h3>
                  <p className="text-[#94A3B8] text-sm">
                    When you first connect your Shopify store, CI performs a full catalog import. This
                    fetches all products, variants, pricing, images, inventory levels, and recent orders
                    using the Shopify Admin API with pagination. Depending on catalog size, this takes
                    30 seconds to a few minutes.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Webhook Registration</h3>
                  <p className="text-[#94A3B8] text-sm">
                    After the initial sync, CI registers webhooks with Shopify for product, order, and
                    inventory events. Shopify sends a POST request to your CI instance whenever a
                    change occurs — no polling required.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Real-Time Updates</h3>
                  <p className="text-[#94A3B8] text-sm">
                    As changes happen in Shopify — new products, price updates, orders, inventory
                    adjustments — webhooks fire and CI processes them within seconds. Each webhook
                    payload is verified via HMAC before processing.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Conflict Resolution</h3>
                  <p className="text-[#94A3B8] text-sm">
                    If a webhook arrives out of order, CI uses the <code className="text-[#3EF1C7]">updated_at</code> timestamp
                    to determine the most recent version. Stale updates are discarded. CI always
                    treats Shopify as the source of truth for product data.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Webhook Events */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Webhook Events
        </h2>
        <p className="text-[#94A3B8] mb-6">
          CI subscribes to the following Shopify webhook topics. Each event triggers a specific
          sync handler with the payload shown below.
        </p>
        <div className="space-y-6">
          {webhookEvents.map((item, index) => (
            <Card key={index} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30 font-mono text-xs">
                    {item.event}
                  </Badge>
                </div>
                <p className="text-[#94A3B8] text-sm mb-4">{item.description}</p>
                <div className="relative">
                  <button
                    onClick={() => copyToClipboard(item.payload, `webhook-${index}`)}
                    className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                  >
                    {copiedCode === `webhook-${index}` ? (
                      <Check className="w-4 h-4 text-[#3EF1C7]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#64748B]" />
                    )}
                  </button>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
                    {item.payload}
                  </pre>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Sync Frequency */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Sync Frequency
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Webhook className="w-6 h-6 text-[#3EF1C7] mb-2" />
              <CardTitle className="text-white">Webhook-Driven (Real-Time)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                The primary sync mechanism. Shopify sends webhooks within seconds of any change.
                CI processes them immediately upon receipt. No configuration needed — webhooks
                are registered automatically when you connect your store.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <RefreshCw className="w-6 h-6 text-amber-400 mb-2" />
              <CardTitle className="text-white">Manual Re-Sync</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Trigger a full re-sync from Settings → Shopify → Re-Sync. This re-fetches your
                entire catalog and reconciles it with CI's records. Useful after bulk imports,
                migration, or if you suspect data drift.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-4">
          <div className="relative">
            <button
              onClick={() => copyToClipboard(`# Trigger manual re-sync via API
curl -X POST https://api.commerceindex.ai/v1/shopify/sync \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode": "full", "shop_domain": "mystore.myshopify.com"}'

# Response
{
  "status": "sync_started",
  "sync_id": "sync_abc123",
  "estimated_duration_seconds": 45,
  "products_to_sync": 342
}`, 'manual-sync')}
              className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
            >
              {copiedCode === 'manual-sync' ? (
                <Check className="w-4 h-4 text-[#3EF1C7]" />
              ) : (
                <Copy className="w-4 h-4 text-[#64748B]" />
              )}
            </button>
            <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`# Trigger manual re-sync via API
curl -X POST https://api.commerceindex.ai/v1/shopify/sync \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode": "full", "shop_domain": "mystore.myshopify.com"}'

# Response
{
  "status": "sync_started",
  "sync_id": "sync_abc123",
  "estimated_duration_seconds": 45,
  "products_to_sync": 342
}`}
            </pre>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Troubleshooting
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Search className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Product not appearing in CI</h3>
                  <ul className="space-y-2 text-sm text-[#94A3B8]">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Check the SKU:</strong> Products without a SKU on at least one variant may be skipped during sync. Add a SKU in Shopify and trigger a re-sync.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Check product status:</strong> Only products with status "active" are imported. Draft and archived products are excluded.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Wait for sync:</strong> If the product was just created, give the webhook a few seconds to arrive. Check the sync log in Settings for confirmation.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Webhook className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Webhook delivery failures</h3>
                  <ul className="space-y-2 text-sm text-[#94A3B8]">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Check Shopify webhook settings:</strong> Go to Shopify Admin → Settings → Notifications → Webhooks. Verify the CI endpoint URL is correct and the webhook is active (not failing).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Review delivery attempts:</strong> Shopify shows the last delivery attempt and response code. A 200 means CI received it. 4xx/5xx errors indicate a problem on the CI side.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Shopify retries:</strong> Shopify retries failed webhooks up to 19 times over 48 hours. If all retries fail, the webhook is deleted. You will need to re-register via Settings → Shopify → Reconnect.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <ArrowDownUp className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Inventory mismatch</h3>
                  <ul className="space-y-2 text-sm text-[#94A3B8]">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Trigger a manual sync:</strong> Go to Settings → Shopify → Re-Sync or use the API endpoint above. This reconciles inventory levels across all locations.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Multi-location stores:</strong> CI aggregates inventory across all Shopify locations. If you recently added or removed a location, trigger a re-sync to pick up the change.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Third-party fulfillment:</strong> If you use a 3PL that updates inventory outside Shopify, ensure those changes propagate to Shopify first. CI only syncs from Shopify, not directly from 3PLs.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/shopify-embedded">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Shopify Embedded App →</h3>
                <p className="text-sm text-[#94A3B8]">Use CI inside Shopify Admin</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Messaging Commands →</h3>
                <p className="text-sm text-[#94A3B8]">Full command reference for all channels</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
